import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { codePush } from 'cap-codepush';
import type { IRemotePackage } from 'cap-codepush/dist/esm/package';
import { Capacitor } from '@capacitor/core';
import { WorkManager } from '@/workers/work.manager';

export interface UpdateInfo {
  packageHash?: string;
  version: string;
  description: string;
  remotePackage?: IRemotePackage;
}

const DEFAULT_UPDATE_INFO: UpdateInfo = { version: '', description: '' };

// Milliseconds for 3 hours
const CHECK_INTERVAL = 3 * 60 * 60 * 1000;

// localStorage key name
const LAST_CHECK_KEY = 'update_last_check_time';

export const useUpdateStore = defineStore('update', () => {
  // state
  const hasUpdate = ref(false);
  const updateInfo = ref<UpdateInfo>({ ...DEFAULT_UPDATE_INFO });
  // Read lastCheckTime from localStorage, default to 0 if not found
  const lastCheckTime = ref(parseInt(localStorage.getItem(LAST_CHECK_KEY) || '0'));
  const checkInterval = ref(CHECK_INTERVAL);
  const isTimerActive = ref(false);

  // getters
  const shouldCheck = computed(() => {
    return Date.now() - lastCheckTime.value > checkInterval.value;
  });

  // actions
  function setUpdateAvailable(info: UpdateInfo) {
    hasUpdate.value = true;
    updateInfo.value = info;
    updateLastCheckTime();
  }

  function clearUpdate() {
    hasUpdate.value = false;
    updateInfo.value = { ...DEFAULT_UPDATE_INFO };
  }

  function updateLastCheckTime() {
    const now = Date.now();
    lastCheckTime.value = now;
    localStorage.setItem(LAST_CHECK_KEY, now.toString());
  }

  // Use WorkManager for timer management
  function startUpdateTimer() {
    if (isTimerActive.value) return;
    console.log('Starting update timer, will check every ' + checkInterval.value / 60000 + ' minutes');
    checkForUpdates();
    WorkManager.getInstance().startUpdateWorker(() => {
      console.log('Timer triggered, checking for updates...');
      checkForUpdates();
    }, checkInterval.value);
    isTimerActive.value = true;
  }

  function stopUpdateTimer() {
    if (!isTimerActive.value) return;
    console.log('Stopping update timer');
    WorkManager.getInstance().stopUpdateWorker();
    isTimerActive.value = false;
  }

  // Core method for checking updates
  async function checkForUpdates() {
    // Only check updates in production environment and on native platforms
    if (import.meta.env.VITE_IS_PROD !== 'true' || !Capacitor.isNativePlatform()) {
      console.log('Not in production or not on native platform, skipping update check');
      updateLastCheckTime();
      return false;
    }

    console.log('Checking for updates...');
    return new Promise<boolean>((resolve) => {
      codePush.checkForUpdate((remotePackage) => {
        updateLastCheckTime();

        if (!remotePackage || remotePackage.failedInstall) {
          console.log('No updates available or update failed');
          resolve(false);
          return;
        }

        // Parse update information
        let version = '';
        let description = '';

        if (remotePackage.description) {
          description = remotePackage.description.replace(/\{\d+\.\d+\.\d+\}/, '');
          const match = remotePackage.description.match(/\{(\d+\.\d+\.\d+)}/);
          if (match) {
            version = match[1];
          }
        }

        // Save update information
        setUpdateAvailable({
          packageHash: remotePackage.packageHash,
          version,
          description,
          remotePackage
        });

        console.log('New version found: ' + version);
        resolve(true);
      });
    });
  }

  return {
    // state
    hasUpdate,
    updateInfo,
    lastCheckTime,
    checkInterval,
    isTimerActive,
    // getters
    shouldCheck,
    // actions
    setUpdateAvailable,
    clearUpdate,
    updateLastCheckTime,
    checkForUpdates,
    startUpdateTimer,
    stopUpdateTimer
  };
});
